import { useEffect, useState } from 'react';
import {
  CoachAvailabilityStatusesEnum,
  useGetCoachRosterVenuesLazyQuery,
  useGetUniqueCoachVenueCourtsLazyQuery,
  useGetUserCustomCourtsLazyQuery,
} from 'types/generated/client';
import {
  formatCoachVenueData,
  formatRosterVenueData,
  LocationType,
  VenueAddress,
  VenueSourceType,
} from 'utils/shared/coachBuilder';

interface UseCoachVenuesOptions {
  userId: string | null | undefined;
  skip?: boolean;
  fetchOnMount?: boolean;
}

interface UseCoachVenuesReturn {
  venues: VenueAddress[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  addVenue: (venue: VenueAddress) => void;
}

export const useCoachVenues = ({
  userId,
  skip = false,
  fetchOnMount = true,
}: UseCoachVenuesOptions): UseCoachVenuesReturn => {
  const [venues, setVenues] = useState<VenueAddress[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const [getRosterVenues, { data: rosterData, loading: rosterLoading, error: rosterError }] =
    useGetCoachRosterVenuesLazyQuery();

  const [
    getAvailabilityVenues,
    { data: availabilityData, loading: availabilityLoading, error: availabilityError },
  ] = useGetUniqueCoachVenueCourtsLazyQuery();

  const [getCustomCourts, { data: customData, loading: customLoading, error: customError }] =
    useGetUserCustomCourtsLazyQuery();

  const loading = rosterLoading || availabilityLoading || customLoading;

  // Fetch all venue sources
  const fetchVenues = async () => {
    if (skip || !userId) return;

    try {
      await Promise.all([
        getRosterVenues({
          fetchPolicy: 'cache-and-network',
          variables: { userId: userId },
        }),
        getAvailabilityVenues({
          fetchPolicy: 'cache-and-network',
          variables: { userId: userId },
        }),
        getCustomCourts({
          fetchPolicy: 'cache-and-network',
          variables: { id: userId },
        }),
      ]);
    } catch (err) {
      setError(err as Error);
    }
  };

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchVenues();
    }
  }, [userId, fetchOnMount]);

  // Merge venues when data changes
  useEffect(() => {
    const mergeVenues = () => {
      const allVenues: VenueAddress[] = [];

      // 1. Add roster venues (priority 1)
      if (rosterData?.venueStaffCoaches) {
        const rosterVenues = formatRosterVenueData(rosterData.venueStaffCoaches);
        allVenues.push(...rosterVenues);
      }

      // 2. Add availability history venues (priority 2)
      if (availabilityData?.coachAvailability) {
        const availabilityVenues = formatCoachVenueData(
          availabilityData.coachAvailability,
        ).map((venue) => ({
          ...venue,
          sourceType: VenueSourceType.AvailabilityHistory,
        }));
        allVenues.push(...availabilityVenues);
      }

      // 3. Add custom courts (priority 3)
      if (customData?.usersByPk?.customCourts) {
        const customCourts: VenueAddress[] = customData.usersByPk.customCourts.map(
          (customCourt) => ({
            id: customCourt.id,
            latitude: customCourt?.latitude || null,
            longitude: customCourt?.longitude || null,
            address: customCourt?.fullAddress || '',
            images: null,
            indoorCourtCount: null,
            outdoorCourtCount: null,
            title: null,
            status: CoachAvailabilityStatusesEnum.Active,
            locationType: LocationType.Custom,
            pickleballNets: null,
            sourceType: VenueSourceType.Custom,
          }),
        );
        allVenues.push(...customCourts);
      }

      // Deduplicate by venue ID (first occurrence wins, maintaining priority order)
      const uniqueVenues = allVenues.reduce((acc, venue) => {
        if (!acc.find((v) => v.id === venue.id)) {
          acc.push(venue);
        }
        return acc;
      }, [] as VenueAddress[]);

      setVenues(uniqueVenues);
    };

    mergeVenues();
  }, [rosterData, availabilityData, customData]);

  // Combine errors
  useEffect(() => {
    const combinedError = rosterError || availabilityError || customError;
    if (combinedError) {
      setError(combinedError as Error);
    }
  }, [rosterError, availabilityError, customError]);

  // Add a new venue to the list
  const addVenue = (venue: VenueAddress) => {
    setVenues((prev) => {
      // Check if venue already exists
      if (prev.find((v) => v.id === venue.id)) {
        return prev;
      }
      return [...prev, venue];
    });
  };

  return {
    venues,
    loading,
    error,
    refetch: fetchVenues,
    addVenue,
  };
};
